import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { useUIStore } from '@/stores/uiStore';
import { useWorkspaceStore, selectActiveWorkspace } from '@/stores/workspaceStore';
import { useCreateCapability } from '@/hooks/useCapabilities';
import { CapabilityType, CapabilityStatus } from '@/types';

// ─── Type picker ──────────────────────────────────────────────────────────────

const TYPE_OPTIONS = [
  { value: CapabilityType.SKILL, label: 'Skill' },
  { value: CapabilityType.KNOWLEDGE, label: 'Knowledge' },
  { value: CapabilityType.EXPERIENCE, label: 'Experience' },
  { value: CapabilityType.TOOL, label: 'Tool' },
  { value: CapabilityType.RELATIONSHIP, label: 'Relationship' },
  { value: CapabilityType.CREDENTIAL, label: 'Credential' },
  { value: CapabilityType.DOMAIN, label: 'Domain' },
];

// ─── QuickCapture ─────────────────────────────────────────────────────────────

const QuickCapture: React.FC = () => {
  const history = useHistory();
  const closeQuickCapture = useUIStore((s) => s.closeQuickCapture);
  const activeWorkspace = useWorkspaceStore(selectActiveWorkspace);

  const workspaceId = activeWorkspace?.id ?? '';
  const createMutation = useCreateCapability(workspaceId);

  const [title, setTitle] = useState('');
  const [summary, setSummary] = useState('');
  const [type, setType] = useState<CapabilityType>(CapabilityType.SKILL);
  const [strength, setStrength] = useState(50);
  const [tagInput, setTagInput] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [expanded, setExpanded] = useState(false);
  const [openAndEdit, setOpenAndEdit] = useState(false);

  const titleRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    titleRef.current?.focus();
  }, []);

  // Keyboard: Escape closes, Ctrl+Enter submits
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        closeQuickCapture();
      }
    },
    [closeQuickCapture]
  );

  const addTag = () => {
    const tag = tagInput.trim().toLowerCase().replace(/\s+/g, '-');
    if (tag && !tags.includes(tag)) {
      setTags((prev) => [...prev, tag]);
    }
    setTagInput('');
  };

  const removeTag = (tag: string) => setTags((prev) => prev.filter((t) => t !== tag));

  const handleTagKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addTag();
    } else if (e.key === 'Backspace' && tagInput === '' && tags.length > 0) {
      setTags((prev) => prev.slice(0, -1));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim() || !workspaceId) {
      return;
    }

    const capability = await createMutation.mutateAsync({
      workspaceId,
      title: title.trim(),
      summary: summary.trim() || null,
      content: JSON.stringify({ type: 'doc', content: [] }),
      type,
      status: CapabilityStatus.ACTIVE,
      tags,
      strength,
      evidence: [],
      collectionIds: [],
      metadata: {},
    });

    closeQuickCapture();

    if (openAndEdit) {
      history.push(`/capabilities/${capability.id}`);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={closeQuickCapture}
        aria-hidden="true"
      />

      {/* Panel */}
      <div
        className="relative w-full max-w-lg bg-white dark:bg-surface-900 rounded-2xl shadow-panel border border-surface-200 dark:border-surface-800 animate-slide-in-up"
        onKeyDown={handleKeyDown}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-surface-200 dark:border-surface-800">
          <div className="flex items-center gap-2">
            <svg className="w-4 h-4 text-accent-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <circle cx="12" cy="12" r="3"/><circle cx="5" cy="6" r="2"/><circle cx="19" cy="6" r="2"/>
              <line x1="12" y1="12" x2="5" y2="6"/><line x1="12" y1="12" x2="19" y2="6"/>
            </svg>
            <h3 className="text-sm font-semibold text-surface-900 dark:text-white">Quick Capture</h3>
          </div>
          <button
            type="button"
            onClick={closeQuickCapture}
            className="p-1 rounded hover:bg-surface-100 dark:hover:bg-surface-800 text-surface-400 transition-colors"
            aria-label="Close"
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {/* Title */}
          <input
            ref={titleRef}
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Capability title…"
            required
            className="w-full text-base font-medium text-surface-900 dark:text-white bg-transparent border-b border-surface-200 dark:border-surface-700 pb-2 outline-none focus:border-primary-500 placeholder:text-surface-300 dark:placeholder:text-surface-600 transition-colors"
          />

          {/* Type selector */}
          <div className="flex gap-1.5 flex-wrap">
            {TYPE_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => setType(opt.value)}
                className={[
                  'flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium transition-all',
                  type === opt.value
                    ? 'bg-primary-700 text-white'
                    : 'bg-surface-100 dark:bg-surface-800 text-surface-600 dark:text-surface-400 hover:bg-surface-200 dark:hover:bg-surface-700',
                ].join(' ')}
              >
                {opt.label}
              </button>
            ))}
          </div>

          {/* Expand for more fields */}
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="flex items-center gap-1 text-xs text-surface-400 hover:text-surface-600 transition-colors"
          >
            <svg
              className={`w-3.5 h-3.5 transition-transform ${expanded ? 'rotate-180' : ''}`}
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              aria-hidden="true"
            >
              <path d="m6 9 6 6 6-6" />
            </svg>
            {expanded ? 'Fewer options' : 'More options'}
          </button>

          {expanded && (
            <div className="space-y-3 animate-fade-in">
              {/* Summary */}
              <textarea
                value={summary}
                onChange={(e) => setSummary(e.target.value)}
                placeholder="Brief description…"
                rows={2}
                className="w-full rounded-lg border border-surface-300 dark:border-surface-600 bg-white dark:bg-surface-900 text-sm px-3 py-2 outline-none focus:border-primary-500 resize-none"
              />

              {/* Strength */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-xs font-medium text-surface-600 dark:text-surface-400">
                    Strength
                  </label>
                  <span className="text-xs font-mono text-accent-600 dark:text-accent-400">
                    {strength}%
                  </span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={strength}
                  onChange={(e) => setStrength(parseInt(e.target.value, 10))}
                  className="w-full accent-accent-500"
                />
              </div>

              {/* Tags */}
              <div>
                <label className="text-xs font-medium text-surface-600 dark:text-surface-400 block mb-1">
                  Tags
                </label>
                <div className="flex flex-wrap gap-1.5 p-2 rounded-lg border border-surface-300 dark:border-surface-600 min-h-[38px]">
                  {tags.map((tag) => (
                    <span
                      key={tag}
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-primary-100 dark:bg-primary-900/40 text-primary-700 dark:text-primary-300 text-xs"
                    >
                      #{tag}
                      <button
                        type="button"
                        onClick={() => removeTag(tag)}
                        className="hover:text-red-500 transition-colors"
                        aria-label={`Remove tag ${tag}`}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                  <input
                    type="text"
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyDown={handleTagKeyDown}
                    onBlur={addTag}
                    placeholder={tags.length === 0 ? 'Add tags (Enter to confirm)' : ''}
                    className="flex-1 min-w-24 bg-transparent text-xs outline-none placeholder:text-surface-300 dark:placeholder:text-surface-600"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center gap-2 pt-1">
            <label className="flex items-center gap-1.5 text-xs text-surface-500 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={openAndEdit}
                onChange={(e) => setOpenAndEdit(e.target.checked)}
                className="rounded accent-primary-700"
              />
              Open after saving
            </label>
            <div className="flex gap-2 ml-auto">
              <button
                type="button"
                onClick={closeQuickCapture}
                className="px-3 py-1.5 text-sm text-surface-600 dark:text-surface-400 hover:bg-surface-100 dark:hover:bg-surface-800 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={!title.trim() || createMutation.isPending || !workspaceId}
                className="px-4 py-1.5 text-sm font-medium text-white bg-accent-500 hover:bg-accent-600 rounded-lg transition-colors disabled:opacity-60"
              >
                {createMutation.isPending ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default QuickCapture;
